import React, { useState, useEffect } from "react";
import { HelmetProvider } from "react-helmet-async";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import ReactGA from "react-ga4";
import { LanguageProvider, useLanguage } from "./LanguageContext";
import { CookieProvider } from "./CookieContext";
import NavHeader from "./components/nav-header";
import NavFooter from "./components/nav-footer";
import CookieBanner from "./components/cookie-banner";
import ComponentsPage from "./pages/Components";
import HomePage from "./pages/Home";
import JobsPage from "./pages/Jobs";

const AppContent = () => {
  const { navigationOptions, loading, currentLocale } = useLanguage();
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState(location.pathname);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // check consents & initialize google analytics
  useEffect(() => {
    const consent = localStorage.getItem("analyticsConsent");

    // only send pageview if GA is initialized and consent is accepted
    if (consent === "accepted" && typeof ReactGA.ga === "function") {
      ReactGA.send({ hitType: "pageview", page: location.pathname });
    }
  }, [location]);

  useEffect(() => {
    if (currentPage !== location.pathname) {
      setIsTransitioning(true);

      // page transition delay
      setTimeout(() => {
        window.scrollTo({ top: 0 });
        setCurrentPage(location.pathname);
        setTimeout(() => setIsTransitioning(false), 600);
      }, 700);
    }
  }, [location, currentPage]);

  if (loading) return <div>Loading...</div>;

  return (
    <>
      {navigationOptions && <NavHeader options={navigationOptions} />}
      <article className="main" role="main">
        <div
          className={`page-transition ${
            isTransitioning ? "fade-out" : "fade-in"
          }`}
        >
          <Routes key={currentPage}>
            <Route
              path="/"
              element={<Navigate to={`/${currentLocale}`} replace />}
            />
            <Route path="/:locale" element={<HomePage />} />
            <Route path="/:locale/jobs/*" element={<JobsPage />} />
            <Route path="/:locale/*" element={<ComponentsPage />} />
            <Route path="*" element={<ComponentsPage />} />
          </Routes>
        </div>
      </article>
      {navigationOptions && <NavFooter options={navigationOptions} />}
    </>
  );
};

const App = () => (
  <HelmetProvider>
    <LanguageProvider>
      <CookieProvider>
        <Router>
          <AppContent />
          <CookieBanner />
        </Router>
      </CookieProvider>
    </LanguageProvider>
  </HelmetProvider>
);

export default App;
